import {
  PROFILE_SECURITY_BACKUP_FILE_LINES,
  PROFILE_SECURITY_BACKUP_FILE_NAME,
  PROFILE_SECURITY_CODE_LENGTH,
  PROFILE_SECURITY_COPY,
  PROFILE_SECURITY_DEFAULT_EXPIRES_MINUTES,
  PROFILE_SECURITY_EMPTY_PASSWORD_FORM,
  PROFILE_SECURITY_EMPTY_RECONFIGURE_FORM,
  PROFILE_SECURITY_PASSWORD_CHANGE_ERRORS,
  PROFILE_SECURITY_PASSWORD_REQUIREMENT_LABELS,
  PROFILE_SECURITY_RECONFIGURE_ERROR_MESSAGES
} from '../constants/profileSecurityConstants'

export function passwordChangeErrorMessage(error) {
  return (
    PROFILE_SECURITY_PASSWORD_CHANGE_ERRORS[error?.message] ||
    error?.message ||
    'No fue posible actualizar.'
  )
}

export function getReconfigureMfaErrorMessage(error) {
  return (
    PROFILE_SECURITY_RECONFIGURE_ERROR_MESSAGES[error?.message] ||
    error?.message ||
    PROFILE_SECURITY_COPY.reconfigureFallback
  )
}

export function buildEmptyPasswordForm() {
  return { ...PROFILE_SECURITY_EMPTY_PASSWORD_FORM }
}

export function buildEmptyReconfigureForm() {
  return { ...PROFILE_SECURITY_EMPTY_RECONFIGURE_FORM }
}

export function buildPasswordChecks(password, passwordConfirmation) {
  return {
    length: password.length >= 8 && password.length <= 15,
    lower: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
    match: passwordConfirmation.length > 0 && password === passwordConfirmation
  }
}

export function buildPasswordRequirements(passwordChecks) {
  return PROFILE_SECURITY_PASSWORD_REQUIREMENT_LABELS.map((requirement) => ({
    ...requirement,
    isMet: passwordChecks[requirement.key]
  }))
}

export function buildVerificationDigits(value) {
  return Array.from({ length: PROFILE_SECURITY_CODE_LENGTH }, (_, index) => value[index] || '')
}

export function canSubmitVerificationCode(value) {
  return new RegExp(`^\\d{${PROFILE_SECURITY_CODE_LENGTH}}$`).test(value)
}

export function focusCodeInput(refs, index) {
  refs.current[index]?.focus()
  refs.current[index]?.select()
}

export function buildHandleCodeChange(currentDigits, setValue, refs) {
  return (index, value) => {
    const sanitizedDigits = value.replace(/\D/g, '')
    if (!sanitizedDigits) {
      const nextDigits = [...currentDigits]
      nextDigits[index] = ''
      setValue(nextDigits.join(''))
      return
    }

    const nextDigits = [...currentDigits]
    for (
      let offset = 0;
      offset < sanitizedDigits.length && index + offset < PROFILE_SECURITY_CODE_LENGTH;
      offset += 1
    ) {
      nextDigits[index + offset] = sanitizedDigits[offset]
    }

    setValue(nextDigits.join(''))
    window.requestAnimationFrame(() => {
      focusCodeInput(refs, Math.min(index + sanitizedDigits.length, PROFILE_SECURITY_CODE_LENGTH - 1))
    })
  }
}

export function buildHandleCodeKeyDown(digits, setValue, refs) {
  return (index, event) => {
    if (event.key === 'Backspace') {
      if (digits[index]) {
        const nextDigits = [...digits]
        nextDigits[index] = ''
        setValue(nextDigits.join(''))
        event.preventDefault()
        return
      }

      if (index > 0) {
        const nextDigits = [...digits]
        nextDigits[index - 1] = ''
        setValue(nextDigits.join(''))
        event.preventDefault()
        window.requestAnimationFrame(() => {
          focusCodeInput(refs, index - 1)
        })
      }
      return
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault()
      focusCodeInput(refs, index - 1)
      return
    }

    if (event.key === 'ArrowRight' && index < PROFILE_SECURITY_CODE_LENGTH - 1) {
      event.preventDefault()
      focusCodeInput(refs, index + 1)
    }
  }
}

export function buildHandleCodePaste(setValue, refs) {
  return (event) => {
    const pasted = event.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, PROFILE_SECURITY_CODE_LENGTH)
    if (!pasted) return

    event.preventDefault()
    setValue(pasted)
    window.requestAnimationFrame(() => {
      focusCodeInput(refs, Math.min(pasted.length, PROFILE_SECURITY_CODE_LENGTH - 1))
    })
  }
}

export function buildBackupCodesFileContent(codes) {
  return [
    PROFILE_SECURITY_BACKUP_FILE_LINES.title,
    `Generados: ${new Date().toLocaleString()}`,
    '',
    PROFILE_SECURITY_BACKUP_FILE_LINES.safeMessage,
    '',
    ...codes
  ].join('\n')
}

export function downloadBackupCodesFile(codes) {
  const content = buildBackupCodesFileContent(codes)
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = PROFILE_SECURITY_BACKUP_FILE_NAME
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

export function getExpiresInMinutes(value) {
  return value || PROFILE_SECURITY_DEFAULT_EXPIRES_MINUTES
}

export function remainingBackupCodesLabel(count) {
  return `${count} restantes`
}

export function emailCardDescription({ emailMfaEnabled, profileEmail }) {
  if (emailMfaEnabled) {
    return `En cada inicio de sesión le enviaremos un código a ${profileEmail || 'su correo'}, salvo que este dispositivo esté recordado.`
  }

  return 'Cuando su correo esté verificado, será el método principal para entrar. Si no le llega, pida a un administrador que lo corrija.'
}

export function authenticatorCardDescription({ mfaEnabled, mfaTrustDeviceDays }) {
  if (!mfaEnabled) return PROFILE_SECURITY_COPY.authenticatorCardDescriptionOptional

  if (mfaTrustDeviceDays > 0) {
    const dayLabel = mfaTrustDeviceDays === 1 ? 'día' : 'días'
    return `La app autenticadora está activa. Si cambia de celular o de app, reconfigúrela aquí: el factor actual sigue activo hasta que confirme el código nuevo. Su empresa puede recordar este dispositivo por ${mfaTrustDeviceDays} ${dayLabel}.`
  }

  return 'La app autenticadora está activa. Puede usarla en lugar del código del correo. Si cambia de celular o de app, reconfigúrela aquí: el factor actual sigue activo hasta que confirme el código nuevo.'
}

export function recoveryCardDescription({ mfaEnabled, backupRemaining, hasFreshBackupCodes }) {
  if (hasFreshBackupCodes) return PROFILE_SECURITY_COPY.recoveryCardDescriptionSaveNow
  if (!mfaEnabled) return PROFILE_SECURITY_COPY.recoveryCardDescriptionPending
  return `Quedan ${backupRemaining} códigos. Si los perdió, regenere un set nuevo (los anteriores dejan de funcionar).`
}

export function buildSecurityStatusItems({
  emailActive,
  mfaEnabled,
  backupRemaining,
  hasFreshBackupCodes
}) {
  return [
    {
      key: 'password',
      label: PROFILE_SECURITY_COPY.statusPassword,
      value: PROFILE_SECURITY_COPY.statusPasswordValue,
      tone: 'active'
    },
    {
      key: 'email',
      label: PROFILE_SECURITY_COPY.statusEmail,
      value: emailActive
        ? PROFILE_SECURITY_COPY.statusEmailActive
        : PROFILE_SECURITY_COPY.statusEmailPending,
      tone: emailActive ? 'active' : 'pending'
    },
    {
      key: 'authenticator',
      label: PROFILE_SECURITY_COPY.statusAuthenticator,
      value: mfaEnabled
        ? PROFILE_SECURITY_COPY.statusAuthenticatorActive
        : PROFILE_SECURITY_COPY.statusAuthenticatorOptional,
      tone: mfaEnabled ? 'active' : 'optional'
    },
    {
      key: 'recovery',
      label: PROFILE_SECURITY_COPY.statusRecovery,
      value: hasFreshBackupCodes
        ? PROFILE_SECURITY_COPY.statusRecoverySaveNow
        : mfaEnabled && backupRemaining > 0
          ? remainingBackupCodesLabel(backupRemaining)
          : PROFILE_SECURITY_COPY.statusRecoveryPending,
      tone: hasFreshBackupCodes
        ? 'pending'
        : mfaEnabled && backupRemaining > 0
          ? 'available'
          : 'pending'
    }
  ]
}
